import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Typography,
  useTheme
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  BCPCircleCheck,
  BCPEnvelope,
  BCPTriangleExclamation
} from "../../components/icons/bcp/BCPIcons";
import {
  getSessionStorage,
  removeSessionStorage
} from "../../components/layout/cookies/CookieFunctions";
import InfoContainer from "../../components/layout/InfoContainer";
import ModalLoadingMessage from "../../components/modals/ModalLoadingMessage";
import { refreshTokens } from "../../components/user/BCPAuth";
import { delay, getData } from "../../components/utilities/UtilityFunctions";
import SubscriptionComplete from "../subscription/SubscriptionComplete";

export default function StripeSessionConfirmation() {
  let stripeSession = getSessionStorage("stripe_session");

  const theme = useTheme();

  if (stripeSession) {
    try {
      stripeSession = JSON.parse(stripeSession);
    } catch (error) {
      console.error("Error parsing stripe session:", error);
      stripeSession = null;
    }
  }

  const sessionId = stripeSession?.id;

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(
    "Checking your subscription status. Please do not close this page or go back..."
  );
  const [status, setStatus] = useState("open");
  const [subscriptionId, setSubscriptionId] = useState(null);

  // Function to poll the session status
  const pollSessionStatus = async (sessionId, attempt = 0) => {
    if (attempt >= 30) {
      setMessage(
        "Your subscription could not be verified in time, please contact support."
      );
      setStatus("error");
      return;
    }

    try {
      const apiStatus = await getData(`/stripe/checkoutsessions/${sessionId}`);

      if (apiStatus?.status === "error") {
        throw new Error("Stripe session not found or invalid. ");
      } else if (apiStatus?.status === "open") {
        await delay(attempt <= 2 ? 2000 : 3000); // Delay for 2 seconds for first 2 attempts, then 3 seconds
        await pollSessionStatus(sessionId, attempt + 1);
      } else if (apiStatus?.status === "complete") {
        setSubscriptionId(apiStatus?.subscriptionId || null);
        setMessage(
          "Subscription confirmation successful! You may now close this page."
        );
        setStatus("complete");
      }
    } catch (error) {
      setMessage(
        `${error || ""}An error occurred while checking subscription status. If this issue persists, please contact support.`
      );
      setStatus("error");
    }
  };

  // Check if sessionId is available and start polling
  useEffect(() => {
    const checkSession = () => {
      if (!sessionId) {
        setMessage(
          "No subscription found. You can view your subscriptions in your account or organization settings. If you believe this is an error, please contact support."
        );
        setStatus("error");
        return;
      }

      if (status === "open") {
        pollSessionStatus(sessionId);
      }
    };

    checkSession();
    // eslint-disable-next-line
  }, []);

  // Handle status changes
  useEffect(() => {
    if (status === "open") {
      return; // Still polling, do nothing
    } else {
      setLoading(false);

      if (status === "complete") {
        removeSessionStorage("stripe_session");

        // Refresh tokens if the session is complete
        const refresh = async () => {
          await refreshTokens();
        };

        refresh();
      }
    }
  }, [status]);

  return (
    <Box width={"100%"}>
      <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
        <Grid container sx={{ gap: 2, p: 2 }}>
          <Grid item xs={12} md={9}>
            <Typography
              variant="h2"
              component="h3"
              fontWeight="700"
              gutterBottom
            >
              Subscription Confirmation
            </Typography>
          </Grid>
        </Grid>
        {loading ? (
          <ModalLoadingMessage open message={message} />
        ) : (
          <Grid item xs={12}>
            <InfoContainer
              title={
                status === "complete" ? (
                  <>
                    <BCPCircleCheck color={theme?.palette?.success.main} />{" "}
                    Success!
                  </>
                ) : (
                  <>
                    <BCPTriangleExclamation
                      color={theme?.palette?.error.main}
                    />{" "}
                    Error Confirming Subscription
                  </>
                )
              }
              body={message || "Unknown error occurred"}
              ActionButton={
                <>
                  {status === "complete" ? (
                    <>
                      <Divider sx={{ mt: 2 }} />

                      {subscriptionId ? (
                        <SubscriptionComplete subscriptionId={subscriptionId} />
                      ) : (
                        <LinearProgress />
                      )}
                    </>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 2,
                        flexWrap: "wrap"
                      }}
                    >
                      <Button
                        variant="contained"
                        href={"/support"}
                        startIcon={<BCPEnvelope />}
                        color={"error"}
                      >
                        {"Contact Support"}
                      </Button>
                    </Box>
                  )}
                </>
              }
            />
          </Grid>
        )}
      </Container>
    </Box>
  );
}
